// The shared chrome and head for every ticker surface.
//
// The homepage, /barn/:slug/ticker and /ticker/:state are the same product
// showing the same thing at three scopes. They had three mastheads, three
// stylesheets and three sets of markup, so they looked like three different
// sites. These are the two pieces they now share.
//
// Presentational only — no fetching, no state, no scope. See BandBoard's header
// for why the data layer deliberately stays separate: the homepage is
// prerendered and must not touch the Supabase client, while the barn and state
// pages are ordinary SPA routes with React Query and Realtime.

import { CATTLE_VIEWS, type CattleView, type MarketStatus } from "@/lib/rbCattleTicker";
import { useInApp } from "@/rbapp/inApp";
import Footer from "../Footer";
import "../rbnext.css";

import { siteHomeHref } from "@/lib/siteHost";

import RbIndexStrip from "./RbIndexStrip";
import TickerChrome from "./TickerChrome";

/**
 * The navy head: title, scope badge, live state, the latency line, filters.
 *
 * `right` and `filters` are slots rather than props-per-control because the
 * three surfaces genuinely differ there — the homepage carries scope/state/barn
 * pickers and a Board|Map toggle, a barn page needs neither since the barn IS
 * the page. Forcing one prop shape would mean a barn page passing nulls for
 * three pickers it will never have.
 */
export function TickerHead({
  title,
  accent,
  badge,
  meta,
  status,
  right,
  filters,
  date,
  brand,
}: {
  /** "Cattle" — the plain part of the title. */
  title: string;
  /** "Ticker" — the gold part. Set in Anton with the title. */
  accent?: string;
  /** The scope, said once: "U.S.", a state code, or a barn. */
  badge?: string;
  /** The latency line. Says what the freshness actually is. */
  meta?: React.ReactNode;
  /** Null until the client has checked — the server cannot know whether the
   *  ring is running, and a prerendered document that claims "Live" would
   *  claim it all weekend. */
  status?: MarketStatus | null;
  right?: React.ReactNode;
  filters?: React.ReactNode;
  /**
   * The day the page is showing, as a big plate beside the title (owner,
   * 2026-09-13: "make the date that people are looking at on all tickers much
   * more visible"). `day` is ISO; `label` is what that day is — "Today's sales",
   * "Saturday's sales" (see dayHeadline). Omit on pages that show no single day.
   */
  date?: { day: string | null; label?: string | null };
  /**
   * A brand row across the top of the head — the two marks.
   *
   * ⚠️ FOR SURFACES WITH NO MASTHEAD ABOVE THEM, which today means the phone
   * app. On the website the marks live in TickerChrome, and putting them here
   * as well would show them twice on the same screen. The app has no chrome (a
   * web masthead's nav is the app's tab bar), so without this the RaisedBeef and
   * DVAuction marks appear nowhere — and the DVAuction one is the attribution
   * for the data the whole board is built on, not decoration. Owner, 2026-09-16:
   * "incorporate the RaisedBeef and DVAuction logo."
   *
   * It sits INSIDE this navy block rather than in a bar above it. A separate bar
   * was the first attempt and it read as two navy mastheads stacked, which cost
   * the phone its first screen of prices.
   */
  brand?: React.ReactNode;
}) {
  return (
    <div className="rbtx-head">
      {brand && <div className="rbtx-head-brand">{brand}</div>}

      <div className="rbtx-head-top">
        <div className="rbtx-title-wrap">
          <h1 className="rbtx-title">
            {title}
            {accent && (
              <>
                {" "}
                <b>{accent}</b>
              </>
            )}
          </h1>
          {badge && <span className="rbtx-scope-badge">{badge}</span>}
        </div>

        {date && <DatePlate day={date.day} label={date.label ?? null} />}

        <div className="rbtx-head-right">
          {right}
          {/* `visibility: hidden` while unknown rather than absent, so resolving
              open/closed does not shift the title row. */}
          <span
            className="rbtx-live"
            data-state={status ? (status.open ? "open" : "closed") : "unknown"}
          >
            <i aria-hidden="true" />
            {status?.open ? "Live" : "Closed"}
          </span>
        </div>
      </div>

      {meta && <div className="rbtx-meta">{meta}</div>}
      {filters && <div className="rbtx-filters">{filters}</div>}
    </div>
  );
}

/**
 * The day a ticker is showing, big enough to read across a room.
 *
 * ⚠️ WHY IT IS A PLATE AND NOT PART OF THE META LINE. The date used to sit in
 * the 12px line under the title — "Saturday's sales · Saturday, September 12 ·
 * Sales resume this morning · 10+ head" — which is where a reader looks last.
 * On a Monday morning a producer glancing at the board could price cattle off
 * Saturday's prints. The day is now the second-biggest thing in the head.
 *
 * TODAY is gold (the page's live colour); any other day is cream, so "this is
 * not today" reads before a single word is read. Before the day is known the
 * plate holds its box invisibly, so resolving it does not shift the title row.
 */
export function DatePlate({ day, label }: { day: string | null; label: string | null }) {
  const d = day ? new Date(`${day.slice(0, 10)}T12:00:00Z`) : null;
  const valid = d !== null && !Number.isNaN(d.getTime());
  const weekday = valid ? d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }) : "";
  const monthDay = valid ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) : "";
  const full = valid
    ? d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })
    : "";
  const isToday = label === "Today's sales";
  return (
    <div
      className="rbtx-dateplate"
      data-today={isToday ? "1" : undefined}
      data-state={valid ? undefined : "unknown"}
      role="status"
      aria-label={valid ? `Showing ${label ? `${label}, ` : ""}${full}` : undefined}
      title={full || undefined}
    >
      <span className="rbtx-dateplate-k">{label ?? "Sale day"}</span>
      <span className="rbtx-dateplate-v">
        {weekday}
        <i aria-hidden="true">·</i>
        {monthDay}
      </span>
    </div>
  );
}

/**
 * The page wrapper: navy masthead, optional breadcrumb, content, footer.
 *
 * Breadcrumbs exist on the barn and state tickers and not on the homepage,
 * because the homepage IS the root — a crumb reading "Home / Home" is noise.
 * They are kept on the interior pages: they are real navigation on a page a
 * reader arrives at from search, and they carry the structured trail crawlers
 * read.
 */
export function TickerShell({
  crumbs,
  children,
  noIndexStrip,
}: {
  crumbs?: { label: string; href?: string }[];
  children: React.ReactNode;
  /** ⚠️ OPT OUT, NOT OPT IN. The owner asked for the indexes at the top of
   *  every other page, so the default is on and a page has to say why not.
   *  The wall display is the case that has one: it is a screen on a wall in a
   *  sale barn, read from across a room, and a row of seven small figures is
   *  noise on it. */
  noIndexStrip?: boolean;
}) {
  // Inside the RaisedBeef app (owner, 2026-10-09: "I want all pages in the
  // app") the page is a screen under the app's own tab bar: no masthead, no
  // menu, no index strip (Home carries the indexes), no crumbs, no footer.
  // The page itself — the tool — is untouched.
  const inApp = useInApp();
  if (inApp) {
    return <div className="rbtx rbtx-inapp">{children}</div>;
  }

  return (
    <div className="rbtx">
      <TickerChrome />
      {!noIndexStrip && <RbIndexStrip />}

      {crumbs && crumbs.length > 0 && (
        <nav className="rbtx-crumbs" aria-label="Breadcrumb">
          {crumbs.map((c, i) => (
            <span key={`${c.label}-${i}`}>
              {i > 0 && (
                <span className="rbtx-crumb-sep" aria-hidden="true">
                  /
                </span>
              )}
              {c.href ? <a href={c.href === "/" ? siteHomeHref() : c.href}>{c.label}</a> : <span>{c.label}</span>}
            </span>
          ))}
        </nav>
      )}

      {children}
      {/* One footer, the same on every page (owner, 2026-10-08) — here, so no
          page carries its own. */}
      <Footer />
    </div>
  );
}


/**
 * The class tabs, shared so all three surfaces look and behave alike.
 *
 * `views` IS A PARAMETER but all three surfaces now pass nothing, and that is
 * the good outcome rather than dead flexibility. It existed because the barn
 * and state boards read get_barn_ticker / rpc_state_ticker, whose "All" meant
 * steers + heifers only and which had no cow/bull option at all — so their
 * combined tab had to be labelled "Both" to avoid claiming a breadth the query
 * did not deliver. Both pages moved onto rpc_cattle_ticker on 2026-09-12, where
 * "All" really is all four classes, so one vocabulary now covers every surface
 * and the label means the same thing on each.
 *
 * Kept because the constraint that created it is still out there: get_barn_ticker
 * is unchanged and still serves the shipped native barn apps. Anything wired
 * back to that RPC needs its own tab set, and building this as a fixed list
 * would invite it to be labelled wrongly instead.
 */
export function ClassTabs<T extends string>({
  view,
  onView,
  views,
}: {
  view: T;
  onView: (next: T) => void;
  /** Defaults to the homepage's four. */
  views?: readonly { key: T; label: string }[];
}) {
  const tabs = (views ?? CATTLE_VIEWS) as readonly { key: T; label: string }[];
  return (
    <div className="rbtx-classes" role="group" aria-label="Cattle class">
      {tabs.map((v) => (
        <button
          key={v.key}
          type="button"
          className="rbtx-class"
          data-on={v.key === view ? "1" : undefined}
          aria-pressed={v.key === view}
          onClick={() => onView(v.key)}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}
